(() => {
  'use strict';
  let serial = 0;
  let pending = false;
  const states = new Set();
  const chevron = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
  const check = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m5 12 4 4 10-10"/></svg>';

  function enhance(select) {
    if (select.dataset.motaiSelect || select.closest('.language-switch')) return;
    select.dataset.motaiSelect = '19';
    const labelElement = select.closest('label');
    const label = select.getAttribute('aria-label') || labelElement?.childNodes[0]?.textContent?.trim() || select.name;
    const wrapper = document.createElement('div');
    wrapper.className = 'motai-select';
    select.before(wrapper);
    wrapper.append(select);
    select.classList.add('motai-native-select');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'motai-select-trigger';
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', label);
    const menu = document.createElement('div');
    menu.className = 'motai-select-menu';
    menu.id = 'motai-selector-v19-' + (++serial);
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', label);
    menu.hidden = true;
    button.setAttribute('aria-controls', menu.id);
    wrapper.append(button, menu);
    let entries = [];
    let options = [];
    let fingerprint = '';
    let paintedIndex = -2;
    let search = '';
    let searchTime = 0;
    const state = { wrapper, menu, button, select, sync, placeMenu, close };
    states.add(state);

    function close() {
      menu.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      search = '';
    }
    function placeMenu() {
      const rect = wrapper.getBoundingClientRect();
      const header = document.querySelector('header')?.getBoundingClientRect();
      const footer = document.querySelector('.mobile-contact')?.getBoundingClientRect();
      const top = header && header.top >= 0 && header.bottom < innerHeight / 2 ? Math.max(12, header.bottom + 8) : 12;
      const bottom = footer?.height > 0 && footer.top < innerHeight ? footer.top - 8 : innerHeight - 12;
      const above = Math.max(0, rect.top - top - 8);
      const below = Math.max(0, bottom - rect.bottom - 8);
      const desired = Math.min(280, menu.scrollHeight);
      const up = below < desired && above > below;
      menu.classList.toggle('flip-up', up);
      menu.style.maxHeight = Math.max(44, Math.min(desired, up ? above : below)) + 'px';
    }
    function focusEntry(index) {
      const entry = entries[index];
      if (!entry || entry.disabled) return;
      entry.focus({ preventScroll: true });
      entry.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    function open() {
      if (button.disabled) return;
      for (const item of states) if (item !== state) item.close();
      menu.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      placeMenu();
    }
    function paint() {
      const selected = select.options[select.selectedIndex];
      button.innerHTML = '<span></span>' + chevron;
      button.firstChild.textContent = selected?.textContent || '';
      button.classList.toggle('is-placeholder', !select.value);
      button.disabled = select.disabled || !entries.some(entry => !entry.disabled);
      button.setAttribute('aria-required', String(select.required));
      entries.forEach((entry, index) => {
        const active = index === select.selectedIndex;
        entry.setAttribute('aria-selected', String(active));
        entry.innerHTML = '<span></span>' + (active ? check : '');
        entry.firstChild.textContent = options[index].textContent;
      });
      paintedIndex = select.selectedIndex;
    }
    function choose(index) {
      if (entries[index]?.disabled) return;
      select.selectedIndex = index;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      close();
      if (button.isConnected) button.focus({ preventScroll: true });
    }
    function keyboard(event, index = select.selectedIndex) {
      const enabled = entries.map((entry, i) => entry.disabled ? -1 : i).filter(i => i >= 0);
      if (!enabled.length) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        button.focus({ preventScroll: true });
      } else if (event.key === 'Tab') {
        if (!menu.hidden) {
          close();
          button.focus({ preventScroll: true });
        }
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const wasClosed = menu.hidden;
        open();
        let next;
        if (event.key === 'Home') next = enabled[0];
        else if (event.key === 'End') next = enabled.at(-1);
        else if (wasClosed) next = enabled.includes(select.selectedIndex) ? select.selectedIndex : enabled[0];
        else {
          const at = enabled.indexOf(index);
          next = enabled[(at + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length];
        }
        focusEntry(next);
      } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.altKey && !event.metaKey && !event.isComposing) {
        event.preventDefault();
        const now = Date.now();
        search = (now - searchTime > 1000 ? '' : search) + event.key.toLocaleLowerCase();
        searchTime = now;
        const find = text => enabled.find(i => options[i].textContent.trim().toLocaleLowerCase().startsWith(text));
        let next = find(search);
        if (next === undefined) {
          search = event.key.toLocaleLowerCase();
          next = find(search);
        }
        open();
        if (next !== undefined) focusEntry(next);
      }
    }
    function sync() {
      const current = [...select.options];
      const nextFingerprint = JSON.stringify([select.required, select.disabled, current.map(option => [option.value, option.textContent, option.disabled])]);
      if (nextFingerprint !== fingerprint) {
        fingerprint = nextFingerprint;
        options = current;
        menu.replaceChildren();
        entries = options.map((option, index) => {
          const entry = document.createElement('button');
          entry.type = 'button';
          entry.tabIndex = -1;
          entry.setAttribute('role', 'option');
          entry.disabled = option.disabled || (select.required && !option.value);
          entry.addEventListener('click', () => choose(index));
          entry.addEventListener('keydown', event => keyboard(event, index));
          menu.append(entry);
          return entry;
        });
        paint();
      } else if (paintedIndex !== select.selectedIndex) paint();
    }
    button.addEventListener('click', event => {
      event.preventDefault();
      menu.hidden ? open() : close();
    });
    button.addEventListener('keydown', event => keyboard(event));
    wrapper.addEventListener('focusout', event => {
      if (!wrapper.contains(event.relatedTarget)) close();
    });
    labelElement?.addEventListener('click', event => {
      if (event.target === labelElement) {
        event.preventDefault();
        button.focus();
      }
    });
    select.addEventListener('change', () => {
      sync();
      paint();
      button.removeAttribute('aria-invalid');
    });
    select.addEventListener('invalid', event => {
      event.preventDefault();
      button.setAttribute('aria-invalid', 'true');
      button.focus();
    });
    sync();
  }

  function refresh() {
    pending = false;
    for (const state of states) {
      if (!state.wrapper.isConnected) states.delete(state);
      else state.sync();
    }
    document.querySelectorAll('main select,.modal select').forEach(enhance);
  }
  refresh();
  new MutationObserver(() => {
    if (!pending) {
      pending = true;
      queueMicrotask(refresh);
    }
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('click', event => {
    for (const state of states) if (!state.wrapper.contains(event.target)) state.close();
  });
  document.addEventListener('reset', event => {
    setTimeout(() => {
      for (const state of states) if (state.select.form === event.target) state.select.dispatchEvent(new Event('change', { bubbles: true }));
    }, 0);
  }, true);
  window.addEventListener('resize', () => {
    for (const state of states) if (!state.menu.hidden) state.placeMenu();
  });
})();
